import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Users } from '../../icons';
/**
 * 채택 3단계 ③ 근태 — 화면 머리와 단추를 정본으로(판 A · 「개인」 묶음).
 *
 * ★★★ **아이콘은 `iconLeft` 로 준다 — `children` 에 넣으면 글자와 붙는다.**
 *    `Button` 은 children 을 `<span class="wc-btn__label">` 하나로 감싸므로,
 *    아이콘을 그 안에 함께 넣으면 **둘이 같은 flex 항목**이 되어 `gap: 8px` 이 안 걸린다.
 *    선언은 있는데 안 먹으므로 CSS 를 봐서는 못 잡는다 — 3배 확대 그림에서 잡았다.
 */
import { PageHeader } from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
// ★ 신청 줄은 **좌측 레일**로 갔다(판 A) — 이 화면이 쓰는 것은 「내 근태 내역」 주소 하나다.
import { MY_HISTORY_PATH } from './my-attendance-links';
import { useClockTick } from '../../hooks/useClockTick';
import { getDateLocale } from '../../lib/base-i18n';
import { useWorkStatusStore } from '../../store/workStatusStore';
// 팀 근태(근태 D4) — **볼 수 있는 사람에게만** 길을 낸다. 권한 판정은 서버가 하므로
// 한 번 물어보고 되면 링크를 건다(팀장은 역할이 STAFF 라 역할만 봐서는 가릴 수 없다).
import { getTeamAttendance } from '../../api/hr-team';
// 「내가 무엇을 맡았나」 — 찔러 보지 않고 묻는다(403 을 만들지 않게).
import { getMyOrgScope } from '../../lib/my-org-scope';
// 간주 출근 — 승인된 출장·외근일은 출근한 것으로 본다 (근태 D2, 2026-08-24).
import { DEEMED_KIND_LABEL } from '../../lib/deemed-attendance';
// ★ 일별 집계 조회(`getDailyWork`)는 **「근무 내역」이 제 손으로 부른다** — 이 화면은
//   마감만 부른다. 두 곳이 따로 부르면 같은 하루가 두 값이 될 수 있어서, 부르는 자리를 하나로 뒀다.
import { closeWorkDay } from '../../api/attendance';
import {
  getWorkPolicy, chooseDayStart, WORK_TYPE_LABEL, type WorkPolicyDto,
} from '../../api/hr-schedule';
/**
 * ★★★ 「근무 내역」이 **이 화면으로 왔다** (2026-09-06 · 사용자 지시).
 *    판 B 는 이 절을 「내 근태 내역」에 얹었는데 자리를 잘못 잡았다 — 하루 단위로
 *    「어느 날 몇 시에 왔다 갔나」를 보는 것은 **매일 여는 화면**의 일이고, 그쪽은
 *    **달마다 보는 것**(연차 · 가산 · 낸 신청 · 임금)을 담는다.
 */
import { MyWorkPeriod } from './MyWorkPeriod';
// 길이 표기는 한 곳을 쓴다 — 이 화면이 따로 만들던 것을 걷었다(2026-09-26).
import { formatWorkMinutes } from '../../lib/daily-work';
// 그날에 걸린 신청 — 셈은 **한 곳**이다(두 화면이 제 손으로 펴면 같은 하루가 달라진다).
import { buildRequestsByDate } from '../../lib/att-requests';
import { listLeaves, listBusinessTrips, listOvertime, type LeaveDto, type TripDto, type OvertimeDto } from '../../api/hr';
// 낱말은 보존해 둔 화면의 사전을 그대로 쓴다 — 같은 신청이 화면마다 다른 이름으로 서면 안 된다.
import { tHR } from '../HRPage/HRPage';
import './MyAttendance.css';

/**
 * 내 근태 — 화면 D(출근 전) · E(퇴근) 의 1단계 판. 2026-08-23.
 *
 * 설계: `docs/attendance-round2-sample.html` 화면 D·E · 「계획」 1단계.
 *
 * 지금 세울 수 있는 것만 세운다:
 *   · 오늘 상태(출근 전 / 근무 중 / 퇴근)와 두 시각 — 서버 기록(`/hr/attendance/*`).
 *   · 이번 달 근무일 · 지각 · 휴가 — `/hr/attendance/summary`.
 *   · **퇴근은 출근한 사람만** — 상태바·위젯과 같은 판정.
 *
 * **2026-08-24 (2단계)** — 기준 시각이 사람마다 달라졌다. `/hr/attendance/policy` 가 사다리
 * (개인 배정 → 팀 → 회사 기본 → 코드 09:00)를 태운 결과와 **그 출처**를 함께 준다. 화면은 그
 * 출처를 늘 밝힌다 — 사람마다 다른 값으로 판정하면서 값을 안 보여 주면 판정을 신뢰할 수 없다.
 * 시차출퇴근이면 **그날 출근 시각을 여기서 고른다**(고른 시각이 곧 기준).
 *
 * 아직 없는 것을 **없다고 말한다**:
 *   · 연장은 신청·승인된 만큼만 인정된다(승인 엔드포인트는 5단계).
 *   · 탄력근로의 주 단위 배정표는 아직 없다 — 서버가 `notice` 로 그렇게 알려 준다.
 *   · 소정 초과·연장은 **신청분만** 인정된다. 승인 엔드포인트가 서는 5단계 전까지 이 화면은
 *     시간을 보여 주기만 하고 연장으로 세지 않는다.
 */

/**
 * 출근~(퇴근|지금) 경과 — "7시간 12분". 시각 문자열(HH:MM)만 있으므로 같은 날 기준으로 센다.
 *
 * ★ **표기는 `lib/daily-work.ts` 의 `formatWorkMinutes` 한 곳을 쓴다** (2026-09-26).
 *   종전에는 여기서 따로 만들어 **「7시간 0분」**처럼 0분을 남겼다. 웹에 같은 뜻의 표기가
 *   **아홉 벌** 있고 규칙이 서로 달라(0분 지움 · 분 2자리 패딩 · 천단위) 지금 전부 모으면
 *   **다른 화면의 보이는 글자가 바뀐다** — 그래서 **여기 한 벌만** 옮겼다.
 *   이 함수는 그때까지 그려진 적이 없어 **바뀌는 화면이 없다**(회귀 0).
 *   나머지 여덟을 모으는 것은 「어느 화면의 어느 글자가 어떻게 바뀌나」를 세어 따로 올린다.
 * ⚠ 자정을 넘긴 근무는 음수가 되어 `null` 이다 — `HH:MM` 두 개만으로는 날을 알 수 없다.
 *   야간 근무자 화면에 쓰려면 날짜를 함께 받아야 한다(그 판은 아직 없다).
 */
export function elapsedLabel(checkIn: string | null, checkOut: string | null, now: Date): string | null {
  if (!checkIn) return null;
  const [sh, sm] = checkIn.split(':').map(Number);
  if (sh === undefined || sm === undefined || Number.isNaN(sh) || Number.isNaN(sm)) return null;
  let endMin: number;
  if (checkOut) {
    const [eh, em] = checkOut.split(':').map(Number);
    if (eh === undefined || em === undefined || Number.isNaN(eh) || Number.isNaN(em)) return null;
    endMin = eh * 60 + em;
  } else {
    endMin = now.getHours() * 60 + now.getMinutes();
  }
  const mins = endMin - (sh * 60 + sm);
  if (mins < 0) return null;
  // ★ 0분이면 `formatWorkMinutes` 가 「0분」을 준다 — 찍은 직후를 빈칸으로 두지 않는다.
  return formatWorkMinutes(mins);
}

/**
 * 근태 관리 화면들 — **길이 없으면 만든 게 아니다**(팀 근태에서 얻은 것과 같은 교훈).
 *
 * 이 화면들은 라우트만 있고 어디에서도 링크되지 않아, URL 을 아는 사람만 들어갈 수 있었다.
 * 회사 관리자에게만 보이는 묶음으로 한 자리에 모은다(권한 판정은 라우트의 RoleGuard 와 서버가 한다).
 */
/**
 * ★★ **관리 링크 묶음을 걷어냈다** (2026-08-29 · 판 D).
 *
 *    「직원이 자기 출근을 찍는 화면 한가운데에 회사 설정 열 칸이 있다」 — 시안이 첫 장에서
 *    지적한 그것이 여기 있었다. 판 5 가 목록을 근태로 옮겼지만 **화면 안 카드 묶음**이라
 *    자리는 그대로였고, 그중 다섯은 주소마저 인사였다.
 *
 *    이제 근태 셸(판 A)에 트리가 서고 여덟이 **주소째** 그 안으로 들어왔으므로(판 B·C)
 *    이 묶음은 **같은 것을 두 번 그리는 일**이 됐다. 그래서 지운다.
 *
 * ★ **맨 마지막에 지운 이유** — 트리가 서기 전에 지우면 그 화면들로 가는 길이 한동안 사라진다.
 *   「길이 없으면 만든 게 아니다」의 반대편이다.
 * ⚠ **`teamPeek` 링크는 남긴다** — 팀장은 역할이 STAFF 라 트리의 「근태 관리」를 못 본다.
 *   지우면 팀장이 근태 현황으로 가는 **유일한 길**이 사라진다(트리에 세우는 것은 판 F).
 *
 * ★★★ **다만 «누구에게나 찔러 보던» 것은 고쳤다** (2026-08-31). 위 「유일한 길」은 판 F 가
 *    트리에 `LEAD_GROUP`(맡은 조직 › 근태 현황)을 세우면서 **더 이상 사실이 아니다** —
 *    링크는 팀장에게 덤이고, **직원에게는 되는 적이 없다.** 그런데 종전에는 직원이 이 화면을
 *    열 때마다 `GET /hr/attendance/team` 이 **403** 을 남겼다.
 *    이제 **맡은 것이 있는 사람**(`orgScope`)에게만 묻는다 — 찔러 보지 않고도 알 수 있는 값이다.
 */

export function MyAttendancePage(): JSX.Element {
  const tick = useClockTick();
  const today = useWorkStatusStore((s) => s.today);
  const hydrated = useWorkStatusStore((s) => s.hydrated);
  // 오늘이 간주 출근일(승인된 출장·외근)이면 퇴근이 열린다 — 근태 D2, 2026-08-24.
  const deemed = useWorkStatusStore((s) => s.deemed);
  const error = useWorkStatusStore((s) => s.error);
  const hydrate = useWorkStatusStore((s) => s.hydrate);
  /**
   * 마감이 끝나면 **「근무 내역」에게 다시 부르라고 이른다.**
   * ★★ 달력은 달마다 받은 값을 **캐시**해 두므로, 마감하고도 이 신호가 없으면 그 칸이
   *    「마감 이상」인 채로 남는다 — 사람은 눌렀는데 화면이 안 바뀐 것으로 읽는다.
   */
  const [workReload, setWorkReload] = useState(0);
  const [closing, setClosing] = useState<string | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  /** 팀 근태를 볼 수 있는가 + 팀원 수. 403·404 면 null 이라 링크가 아예 안 생긴다. */
  const [teamPeek, setTeamPeek] = useState<{ name: string; total: number } | null>(null);
  const [policy, setPolicy] = useState<WorkPolicyDto | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [choiceError, setChoiceError] = useState<string | null>(null);
  /**
   * 낸 신청 — 「근무 내역」의 고른 날이 「그날 무슨 신청이 걸렸나」를 적는 데만 쓴다.
   * ★★ **못 읽으면 `null` 로 둔다**(빈 목록이 아니라). 빈 목록을 넘기면 화면이 그날의
   *    승인요청을 **「없음」이라 적는데**, 그건 「모른다」와 다르다.
   * ★ 셋 다 실패해도 화면은 돈다 — 이 칸만 「모름」이 된다.
   */
  const [myRequests, setMyRequests] = useState<{
    leaves: LeaveDto[]; trips: TripDto[]; overtime: OvertimeDto[];
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => { if (!hydrated) void hydrate(); }, [hydrated, hydrate]);
  useEffect(() => {
    let alive = true;
    // 요약·정책 조회 실패는 화면을 막지 않는다 — 오늘 찍는 일이 이 화면의 본래 목적이다.
    getWorkPolicy().then((p) => { if (alive) setPolicy(p); }).catch(() => { /* 무시 */ });
    // ★ **맡은 것이 있는 사람에게만 묻는다** — 직원에게는 되는 적이 없어 403 만 남았다.
    //   그래도 실패는 조용히 넘긴다(404 옛 서버 · 권한이 방금 바뀐 경우).
    void getMyOrgScope().then((sc) => {
      if (!alive || sc === 'NONE') return;
      getTeamAttendance()
        // ★ 전사 범위면 `team` 이 null 이다(2026-08-29 · 판 F) — 그때는 「전사」라고 부른다.
        .then((t) => { if (alive) setTeamPeek({ name: t.team?.name ?? '전사', total: t.summary.total }); })
        .catch(() => { if (alive) setTeamPeek(null); });
    });
    return () => { alive = false; };
  }, [today.checkIn, today.checkOut]);

  /**
   * 낸 신청 셋 — **한 번만** 읽는다(오늘 찍기와 함께 다시 부를 값이 아니다).
   * ★ 셋 중 하나라도 못 읽으면 통째로 `null` — 반쪽짜리 목록으로 「그날은 신청이 없다」고
   *   적으면 그것이 거짓이 된다.
   */
  useEffect(() => {
    let alive = true;
    void Promise.all([listLeaves(), listBusinessTrips(), listOvertime()])
      .then(([l, t, o]) => { if (alive) setMyRequests({ leaves: l, trips: t, overtime: o }); })
      .catch(() => { if (alive) setMyRequests(null); });
    return () => { alive = false; };
  }, []);

  /** 날짜 → 그날 걸린 신청. 못 읽었으면 `undefined` 라 화면이 「모름」으로 그린다. */
  const requestsByDate = useMemo(() => {
    if (!myRequests) return undefined;
    const t = tHR();
    return buildRequestsByDate({
      leaves: myRequests.leaves,
      trips: myRequests.trips,
      overtime: myRequests.overtime,
      typeLabel: (ty) => (t.leave_type as Record<string, string>)[ty] ?? ty,
      statusLabel: (st) => (t.leave_status as Record<string, string>)[st] ?? st,
      overtimeLabel: '연장근로',
    });
  }, [myRequests]);

  /** 시차출퇴근 — 그날 시각 고르기. 고른 시각이 곧 그날의 기준이 된다. */
  const onChoose = async (startTime: string) => {
    if (!policy) return;
    setChoosing(true);
    setChoiceError(null);
    try {
      const res = await chooseDayStart(policy.date, startTime);
      setPolicy({ ...policy, choice: res.choice, baseStart: res.baseStart, baseEnd: res.baseEnd, startSource: 'CHOICE' });
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setChoiceError(
        code === 'ALREADY_CHECKED_IN' ? '이미 출근한 날은 기준 시각을 바꿀 수 없습니다.'
        : code === 'OUT_OF_WINDOW' ? '선택폭 안에서 골라 주세요.'
        : '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.',
      );
    } finally {
      setChoosing(false);
    }
  };

  /**
   * 안 닫힌 날을 **소정 종료 시각으로** 마감한다.
   *
   * ★ 서버가 몰래 닫지 않는다 — 누른 것이 사람의 확인이라 사실이 된다. 그래서 이 버튼이 있다.
   */
  const onClose = async (workDate: string) => {
    setClosing(workDate);
    setCloseError(null);
    try {
      await closeWorkDay(workDate);
      setWorkReload((n) => n + 1);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setCloseError(
        code === 'NOT_CHECKED_IN' ? '그날 출근 기록이 없어 마감할 수 없습니다.'
        : code === 'NOT_PAST_DAY' ? '지난 날만 마감할 수 있습니다.'
        : '마감하지 못했습니다. 잠시 후 다시 시도해 주세요.',
      );
    } finally {
      setClosing(null);
    }
  };

  /** 선택폭을 30분 간격으로 편다 — 08:00 · 08:30 … 10:00. */
  const flexOptions = (() => {
    if (!policy?.canChooseStart || !policy.flexStart || !policy.flexEnd) return [];
    const toMin = (v: string) => Number(v.slice(0, 2)) * 60 + Number(v.slice(3, 5));
    const out: string[] = [];
    for (let m = toMin(policy.flexStart); m <= toMin(policy.flexEnd); m += 30) {
      out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
    }
    return out;
  })();

  const now = new Date(tick * 1000);

  const deemedLabel = deemed ? DEEMED_KIND_LABEL[deemed.kind] : null;
  // ★ **실측이 간주를 이긴다** — 출장 중에 실제로 찍었으면 그 기록이 진실이다.
  // 출근 전이면 null — 화면 머리에 아무것도 안 붙는다.
  const elapsed = elapsedLabel(today.checkIn, today.checkOut, now);
  const state = today.checkOut ? '퇴근'
    : today.checkIn ? '근무 중'
    : deemedLabel ? `${deemedLabel} 중` : '아직 출근 전';


  return (
    <div className="myatt">
      {/*
        ★ 「근태 현황」 길은 **Button 으로 바꾸지 않는다** — `href` 는 페이지를 새로 읽는다.
          SPA 안에서 옮겨 다니는 것은 `Link` 의 일이라, 그대로 두고 자리만 머리로 옮겼다.
      */}
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance')}
        scope={scopeFor('/erp/attendance')}
        title="내 근태"
        /*
          ★ 「지난 시간」을 상태 뒤에 붙인다 (2026-09-26 사용자 결정 — 부품은 있는데 붙인 데가 없었다).
            · 출근 전이면 `elapsedLabel` 이 `null` 이라 **아무것도 안 붙는다**(빈 「 · 」을 만들지 않는다).
            · `useClockTick` 이 이미 이 화면을 분마다 다시 그리므로 **새 타이머가 없다**.
            · 퇴근했으면 출근~퇴근, 아직이면 출근~지금이다.
          ⚠ 두 시각 자체(09:00 ~ —)는 **레일 머리**(`NavBar/RailHead.tsx`)가 그린다. 거기에도
            경과를 붙일지는 «셸을 건드리는 일»이라 따로 정한다 — 여기서는 이 화면만 붙인다.
        */
        description={`${now.toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })} · ${state}${elapsed ? ` · ${elapsed}` : ''}`}
        actions={teamPeek ? (
          <Link className="myatt__teamlink" to="/erp/attendance/team">
            <Users size={14} aria-hidden />
            {/* 2026-10-14 사용자 지시 — 화면 머리 오른쪽의 건수를 지웠다. 링크는 남긴다. */}
            근태 현황
          </Link>
        ) : undefined}
        /*
          ★★ **«원래 규칙» 은 도움말로** (정본 P5 · P3-3). 종전에는 오늘 줄 아래에 규칙 설명이
             두 줄 붙어 화면 첫머리를 먹었다. 「지금의 판정」(오늘이 어떤 날인지)만 화면에 남기고,
             「어떻게 세는가」는 여기로 옮긴다.
        */
        help={{
          title: '이 화면이 세는 규칙',
          body: (
            <>
              · <b>기준 시각</b>은 판정 사다리에서 옵니다 — 개인 배정 → 팀 배정 → 회사 기본 근무제 →
              코드 기본값 09:00. 위에서부터 처음 만나는 것을 씁니다.
              <br />· 소정을 넘겨 일했더라도 <b>연장은 신청·승인된 만큼만</b> 인정됩니다.
              이 화면은 시간을 보여 줄 뿐 연장으로 세지 않습니다 — 신청은 위 <b>연장근로</b>로 합니다.
              <br />· <b>미신청 잔여</b>는 소정 종료를 넘겨 남았지만 연장근로 신청이 없는 시간입니다.
              기록만 되고 수당으로는 세지 않습니다.
            </>
          ),
        }}
      />

      {/*
        ★★★ **오늘 줄은 좌측 레일로 갔다** (2026-09-06 · 판 A · 사용자 지시).
           시각 · 출근 · 퇴근 · 두 단추 · 근무 상태가 `AttShell` 의 「오늘」 상자에 있다.
           **여기 두면 같은 값이 한 화면에 두 번 서고**, 어느 것이 진짜인지 묻게 된다.
        ★ 이 화면에는 **보는 것만** 남는다 — 이번 달 차트 · 다섯 칸 · 마감하지 않은 날.
      */}
      {/* 왜 출근으로 보는지 밝힌다 — 근거 없이 결근이 지워지면 판정을 신뢰할 수 없다. */}
      {deemed && !today.checkIn && (
        <p className="myatt__note myatt__loose">
          오늘은 승인된 <b>{deemedLabel}</b>{deemed.destination ? ` (${deemed.destination})` : ''} 이라
          <b> 출근한 것으로 봅니다</b> — 결근이 아니고 퇴근을 기록할 수 있습니다.
        </p>
      )}

      {error && <p className="myatt__error myatt__loose" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}

      {/* 시차출퇴근 — 그날 고른 시각이 곧 기준이다. 안 고르면 선택폭의 마지막 시각으로 본다
            (안 고른 것이 이득이 되면 아무도 고르지 않는다). */}
      {policy?.canChooseStart && (
        <div className="myatt__flex">
            <div className="myatt__flexhead">
              오늘 출근 시각 고르기
              <span className="myatt__hint">
                {/*
                  ★★★ **고른 시각의 «종업»은 여기 남긴다** (2026-09-08).
                     「오늘 기준 시각은 …」 문장을 걷어내면서 **종업 시각이 화면에서 통째로
                     사라질 뻔했다** — 고르면 종업도 함께 움직인다는 것이 이 기능의 요점인데,
                     그 말을 하던 유일한 자리가 걷어낸 문장이었다.
                     ★ 걷어낸 것은 «늘 서 있던 안내문»이고, 이것은 «고른 사람의 값»이다.
                */}
                선택폭 {policy.flexStart}–{policy.flexEnd}
                {policy.choice
                  ? ` · 고른 시각 ${policy.choice} · 종업 ${policy.baseEnd}`
                  : ' · 아직 안 골랐습니다'}
              </span>
            </div>
            <div className="myatt__chips">
              {flexOptions.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`myatt__chip${policy.choice === t ? ' is-on' : ''}`}
                  onClick={() => { void onChoose(t); }}
                  disabled={choosing || Boolean(today.checkIn)}
                  title={today.checkIn ? '이미 출근한 날은 바꿀 수 없습니다' : undefined}
                >
                  {t}
                </button>
              ))}
            </div>
            {choiceError && <p className="myatt__error" role="alert"><AlertCircle size={14} aria-hidden />{choiceError}</p>}
          </div>
        )}

      {/*
        ★★ 아래 넷은 **«지금의 판정»** 이라 화면에 남는다(정본 P5) — 오늘이 어떤 날인지,
           무엇이 막혔는지, 오늘 어느 시각을 골랐는지. **«원래 규칙»** (기준 시각이 어디서
           오는지 · 연장은 신청분만 인정된다)은 화면에서 걷어내 **화면 머리 도움말**로 옮겼다.
           시안이 오늘 줄 아래를 비워 둔 까닭이 그것이다.
      */}
      {policy?.lateJudged === false && (
        <p className="myatt__note myatt__loose">
          오늘은 <b>지각 판정을 하지 않습니다</b> — {policy.schedule ? `${WORK_TYPE_LABEL[policy.schedule.type] ?? policy.schedule.type} ` : ''}
          정산기간의 총 근로시간으로 봅니다.
        </p>
      )}
      {/*
        ★★ **출처는 화면에 남는다.** 사람마다 다른 값으로 지각을 판정하면서 그 값이 어디서
           왔는지 안 보여 주면 판정을 신뢰할 수 없다 — 이 계약은 검사가 지킨다.
           숫자(09:00 · 18:00)는 위 상자가 이미 적으므로 여기서는 «어디서 왔나» 만 말한다.
      */}
      {/*
        ★★★ **「오늘 기준 시각은 09:00 ~ 18:00 …」 안내는 걷어냈다** (2026-09-08 사용자 지시).
           2026-09-06 에 오늘 줄이 레일로 가면서 «숫자가 화면에서 사라진다»는 이유로 여기에
           다시 적었던 문장이다. 이제 그 줄 자체가 없어졌고(2026-09-08 레일 정리),
           **기준 시각은 「근무 내역」의 하루 상세가 그 날의 값으로 말한다.**
        ★ 위 「근무제 고르기」 칸은 그대로다 — 그건 «고르는 일»이지 «알리는 문장»이 아니다.
      */}
      {policy?.notice && <p className="myatt__note myatt__loose">{policy.notice}</p>}

      {/*
        ★★★ **「신청서는 왼쪽 …에서 냅니다」 안내도 걷어냈다** (2026-09-08 사용자 지시).
           2026-09-06 에 신청 줄을 레일로 옮기며 «어디로 갔는지»를 적어 둔 한 줄이었다.
           ★ 그때의 근거(「없어졌다」와 「옮겼다」는 다르다)는 **옮긴 직후**에 맞는 말이고,
             자리가 굳은 뒤에는 **매일 여는 화면에 남는 안내문**이 된다 — 정본 P8 의 자리다.
      */}
      {/*
        ★★★ **「오늘 시간 구성」은 걷어냈다** (2026-09-08 사용자 지시).
           인정 근로 · 미신청 체류 · 승인 연장 · 휴게 네 칸이 **오늘 하루만** 말하고 있었는데,
           바로 아래 「근무 내역」이 **하루하루를 다 그린다** — 같은 하루가 두 자리에 섰다.
        ★★★ **「마감하지 않은 날」은 «지운» 것이 아니라 «옮겼다»** — 근무 내역 달력의
           **그 칸 안**에 「마감 이상」으로 붙고, 그 날을 누르면 하루 상세에서 마감한다
           (2026-09-08 사용자 지시 — 달력 «아래»가 아니라 «안»이다).
           달력이 이미 그 날들을 그리므로, 별도의 목록을 옆에 또 세울 이유가 없다.
      */}
      {/*
        ── 근무 내역 — 주간 · 월간 (2026-09-06 · 정본 P3-5) ──
        ★★★ **판 B 가 「내 근태 내역」에 얹었던 절을 이리로 옮겼다**(사용자 지시).
           「어느 날 몇 시에 왔다 갔나」는 **매일 여는 화면**에서 보는 것이고,
           그쪽은 달마다 보는 것(연차 · 가산 · 낸 신청 · 임금)을 담는다.
        ★ 아래 두 절(이번 달 근무 차트 · 이번 달 요약)은 **그대로 둔다** — 저쪽은 «달의 합계»,
          이 절은 «하루하루» 라 같은 값이 두 번 서는 것이 아니다.
        ★ 「신청이력」 단추는 **화면을 옮긴다** — 낸 신청 목록은 「내 근태 내역」에 있다.
      */}
      <section className="myatt__period" aria-label="근무 내역">
        <h2 className="myatt__monthhead">
          {/* ★ 「주간이 기본 · 날짜를 누르면 펼쳐진다」 곁말은 걷어냈다(2026-09-08 사용자 지시) —
              기간 단추와 눌리는 칸이 이미 그 말을 한다(정본 P8). */}
          근무 내역
        </h2>
        <MyWorkPeriod
          requestsByDate={requestsByDate}
          onGoRequests={() => { navigate(MY_HISTORY_PATH); }}
          onCloseDay={(d) => { void onClose(d); }}
          closingDate={closing}
          closeError={closeError}
          reloadKey={workReload}
        />
      </section>

      {/*
        ★★★ **「이번 달 근무」(막대 그림)와 「이번 달」(요약 타일) 둘을 걷어냈다**
           (2026-09-06 사용자 지시). 이 화면은 이제 **오늘 + 근무 내역**이다 —
           달의 합계는 「내 근태 내역」이 달마다 말한다.
        ★ 걷어낸 것: `MonthlyWorkChart` 한 벌 · `StatTiles` 다섯 칸(근무일 · 지각 · 휴가 ·
          간주 · 미신청 잔여)와, 막대의 기준선을 유도하던 `standardMinutes`.
        ★ 일별 집계 조회도 이 화면에서는 **끊었다** — 「근무 내역」이 제 손으로 부르고,
          마감 뒤에는 `workReload` 로 다시 부르라고만 이른다.
      */}


      {/*
        ★★ **가산 시간 절은 여기 없다** (2026-09-03 · 정본 P3-3). 종전에는 이 화면과
           「내 임금」이 **서버의 같은 함수 하나**(`splitPremium()`)를 읽어 같은 값을 다른 이름으로
           두 번 그렸고, 「넷을 더하지 마세요」라는 **같은 설명문도 두 벌**이었다.
           이제 「내 근태 내역」 한 곳에만 있다 — 위 「승인 연장」 칸이 그 요약을 대신한다.
      */}
    </div>
  );
}
